import type { InvoiceResponse } from './types';

import {
  QueryFunctionContext,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoice } from './fetchInvoice';

/**
 * Generates the query options for the invoice query so they can be shared between the useQuery hook and prefetching.
 *
 * @param invoiceId - The ID of the invoice to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getInvoiceQueryOptions = ({
  invoiceId,
  options,
}: {
  invoiceId: UUID;
  options?: Omit<UseQueryOptions<InvoiceResponse>, 'queryKey' | 'queryFn'>;
}) => {
  return {
    enabled: Boolean(invoiceId),
    queryFn: async (options: QueryFunctionContext) =>
      fetchInvoice(invoiceId, options),
    queryKey: queryKeysInvoice.get(invoiceId),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for a single invoice with a given invoiceId.
 * Uses the internal endpoint with additional invoice data.
 *
 * @param invoiceId - The ID of the invoice to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoice_internal_invoice_internal__invoice_id__get
 */
export const useQueryInvoice = (
  invoiceId: UUID,
  options?: Omit<UseQueryOptions<InvoiceResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(getInvoiceQueryOptions({ invoiceId, options }));
};
